// frontend/src/services/adminApi.js
import { http } from "./http";

/** Small helper to build query string from filter */
function buildFilterQuery(filter = {}) {
  const q = new URLSearchParams();
  if (filter.conferenceId) q.set("conferenceId", filter.conferenceId);
  if (filter.stationId) q.set("stationId", filter.stationId);
  if (filter.districtId) q.set("districtId", filter.districtId);
  if (filter.churchId) q.set("churchId", filter.churchId);

  // Optional extras (safe to ignore on backend if not implemented)
  if (filter.q) q.set("q", filter.q);
  if (filter.status) q.set("status", filter.status); // active | inactive | all

  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

/** Scope (for RoleRedirect & UI) */
export function getMyAdminScope(token) {
  return http(`/api/admin/me/scope`, { token });
}

/** Learners in scope (optionally filtered by hierarchy) */
export function getAdminLearners(token, filter = {}) {
  const qs = buildFilterQuery(filter);
  return http(`/api/admin/learners${qs}`, { token });
}

/** Stats in scope (optionally filtered by hierarchy) */
export function getAdminStats(token, filter = {}) {
  const qs = buildFilterQuery(filter);
  return http(`/api/admin/stats${qs}`, { token });
}

/* -------------------------------------------------------------------------- */
/* Instructors                                                                */
/* -------------------------------------------------------------------------- */

/**
 * List instructors visible in caller’s scope.
 * Optional filter:
 *  { conferenceId, stationId, districtId, churchId, q, status }
 */
export function listInstructors(token, filter = {}) {
  const qs = buildFilterQuery(filter);
  return http(`/api/admin/instructors${qs}`, { token });
}

/**
 * Fetch one instructor details (assignedChurchIds etc.)
 */
export function getInstructor(token, id) {
  return http(`/api/admin/instructors/${id}`, { token });
}

/**
 * Invite an instructor.
 * payload: {
 *   email: string,
 *   fullName?: string,
 *   phone?: string,
 *   conferenceId?: string,
 *   stationId?: string,
 *   districtId?: string,
 *   churchId?: string
 * }
 *
 * Scope rules are enforced server-side based on the caller’s role.
 */
export function addInstructor(token, payload) {
  return http(`/api/admin/instructors`, {
    method: "POST",
    token,
    body: payload,
  });
}

/**
 * Update instructor status (activate/deactivate)
 * payload: { isActive: boolean }
 */
export function updateInstructorStatus(token, id, isActive) {
  return http(`/api/admin/instructors/${id}`, {
    method: "PATCH",
    token,
    body: { isActive },
  });
}

/**
 * Set instructor church assignments (replaces all)
 * payload: churchIds: string[]
 */
export function setInstructorChurches(token, id, churchIds = []) {
  return http(`/api/admin/instructors/${id}/churches`, {
    method: "PUT",
    token,
    body: { churchIds },
  });
}

/** Delete instructor by users.id (UUID) */
export function deleteInstructor(token, id) {
  return http(`/api/admin/instructors/${id}`, {
    method: "DELETE",
    token,
  });
}

/* -------------------------------------------------------------------------- */
/* Admins (union / conference / district)                                     */
/* -------------------------------------------------------------------------- */

/** List admins visible in the caller’s scope */
export function listAdmins(token) {
  return http(`/api/admin/admins`, { token });
}

/**
 * Create a scoped admin via Clerk invite.
 *
 * payload: {
 *   email: string,
 *   fullName?: string,
 *   level: 'conference' | 'district',
 *   conferenceId?: string,
 *   districtId?: string
 * }
 */
export function createScopedAdmin(token, payload) {
  return http(`/api/admin/admins`, {
    method: "POST",
    token,
    body: payload,
  });
}

/** Delete an admin by users.id (UUID). */
export function deleteAdmin(token, id) {
  return http(`/api/admin/admins/${id}`, {
    method: "DELETE",
    token,
  });
}

/* -------------------------------------------------------------------------- */
/* Lifecycle (if you already added these endpoints)                           */
/* -------------------------------------------------------------------------- */

export function getLifecycleSummary(token) {
  return http(`/api/admin/lifecycle/summary`, { token });
}

export function deactivateLearners(token, userIds = []) {
  return http(`/api/admin/lifecycle/deactivate`, {
    method: "POST",
    token,
    body: { userIds },
  });
}
